// =============================================================================
// RENDER DIRECTIVES (Faza 4 — Jarvis) — dependency-free protocol
// =============================================================================
//
// A *render directive* is a small, declarative, whitelisted description of a
// piece of UI that the AI (Nexus) wants the shell to draw — a metric tile, a
// chart, a table, a timeline, an action button, etc. It is the "generative UI"
// contract: instead of returning prose only, the AI can return a structured
// payload that the shell renders into native widgets.
//
// Design rules (mirrors envelope.ts):
//   1. DEPENDENCY-FREE. The `@insolitum/types` package carries no runtime deps
//      (no Zod). Validation here is hand-rolled and defensive.
//   2. WHITELISTED. The shell only ever renders `kind`s it recognises from the
//      closed `RenderDirectiveKind` union. Anything else is dropped at the
//      boundary (`validateDirective` rejects it) — an LLM cannot inject novel
//      widget kinds, scripts, or raw HTML. This is the security backbone of
//      generative UI: the model proposes data, never code.
//   3. ADDITIVE & OPTIONAL. Producers attach `render_directives?` to existing
//      responses; consumers that don't understand them ignore them. Nothing on
//      the hot path depends on this type.
//   4. EXTENSIBLE BY REGISTRATION. New widget kinds are added to the union here
//      (reviewed), never improvised by a producer. Matches C.2.4 of the plan.
// =============================================================================

/**
 * The closed whitelist of widget kinds the shell knows how to render.
 * Adding a kind is a deliberate, reviewed change to this union (and the shell's
 * `GenerativeSurface` switch) — never something a producer can invent at runtime.
 */
export type RenderDirectiveKind =
  | 'metric' // single KPI / stat tile
  | 'chart' // bar / line / area / pie series
  | 'table' // tabular rows + columns
  | 'timeline' // ordered events with timestamps
  | 'list' // simple ordered/unordered item list
  | 'callout' // highlighted note (info/success/warn/danger)
  | 'actions' // a row of action buttons (each gated downstream)
  | 'markdown'; // safe, restricted markdown text block

/** Visual intent shared by several widgets (callouts, metrics, actions). */
export type RenderTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/** Supported chart shapes. Kept small and dependency-free (rendered as SVG). */
export type ChartVariant = 'bar' | 'line' | 'area' | 'pie';

// -----------------------------------------------------------------------------
// Per-kind payloads
// -----------------------------------------------------------------------------

export interface MetricDirective {
  kind: 'metric';
  /** Short label, e.g. "OEE", "Koszt pracy", "Stockout %". */
  label: string;
  /** Primary value, already formatted by the producer (e.g. "82%", "1 240 zł"). */
  value: string;
  /** Optional secondary line (e.g. "vs 78% w zeszłym tygodniu"). */
  sublabel?: string;
  /** Optional trend hint for an arrow/colour cue. */
  trend?: 'up' | 'down' | 'flat';
  tone?: RenderTone;
}

export interface ChartSeriesPoint {
  /** Category / x-axis label. */
  label: string;
  /** Numeric value. */
  value: number;
}

export interface ChartDirective {
  kind: 'chart';
  variant: ChartVariant;
  title?: string;
  /** A single series of points. Multi-series is intentionally out of scope v1. */
  points: ChartSeriesPoint[];
  /** Optional unit suffix for tooltips/labels (e.g. "zł", "%", "szt."). */
  unit?: string;
}

export interface TableDirective {
  kind: 'table';
  title?: string;
  columns: string[];
  /** Each row is an array of cell strings aligned to `columns`. */
  rows: string[][];
}

export interface TimelineItem {
  /** ISO timestamp or human label (rendered as-is). */
  at: string;
  title: string;
  description?: string;
  tone?: RenderTone;
}

export interface TimelineDirective {
  kind: 'timeline';
  title?: string;
  items: TimelineItem[];
}

export interface ListDirective {
  kind: 'list';
  title?: string;
  ordered?: boolean;
  items: string[];
}

export interface CalloutDirective {
  kind: 'callout';
  tone: RenderTone;
  title?: string;
  text: string;
}

export interface RenderAction {
  /** Stable action id the shell maps to a handler / skill invocation. */
  action: string;
  label: string;
  tone?: RenderTone;
  /**
   * Whether this action mutates money/people/devices. When true the shell MUST
   * route it through a propose→confirm guardrail before dispatching (G.2 #3).
   */
  requiresConfirm?: boolean;
  /** Opaque params forwarded to the handler (validated downstream, not here). */
  params?: Record<string, unknown>;
}

export interface ActionsDirective {
  kind: 'actions';
  title?: string;
  actions: RenderAction[];
}

export interface MarkdownDirective {
  kind: 'markdown';
  /** Restricted markdown — the shell renders a safe subset (no raw HTML). */
  text: string;
}

/** The discriminated union of every renderable directive. */
export type RenderDirective =
  | MetricDirective
  | ChartDirective
  | TableDirective
  | TimelineDirective
  | ListDirective
  | CalloutDirective
  | ActionsDirective
  | MarkdownDirective;

/** The set of valid kinds, for runtime membership checks. */
export const RENDER_DIRECTIVE_KINDS: readonly RenderDirectiveKind[] = [
  'metric',
  'chart',
  'table',
  'timeline',
  'list',
  'callout',
  'actions',
  'markdown',
] as const;

const RENDER_TONES: readonly RenderTone[] = [
  'neutral',
  'info',
  'success',
  'warning',
  'danger',
];

const CHART_VARIANTS: readonly ChartVariant[] = ['bar', 'line', 'area', 'pie'];

/** Hard cap on directives per response — defends against runaway LLM output. */
export const MAX_RENDER_DIRECTIVES = 12;

// -----------------------------------------------------------------------------
// Validation — the security boundary. Reject anything not on the whitelist or
// not shaped correctly. Returns the value narrowed, or null if invalid.
// -----------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isStr(v: unknown): v is string {
  return typeof v === 'string';
}

function isStrArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every(isStr);
}

function toneOk(v: unknown): boolean {
  return v === undefined || (isStr(v) && (RENDER_TONES as string[]).includes(v));
}

/**
 * Validate a single directive against the whitelist. Pure, throws nothing.
 * Returns the directive (typed) when valid, or `null` to be dropped by the
 * caller. Unknown kinds, missing required fields, or wrong shapes → null.
 */
export function validateDirective(input: unknown): RenderDirective | null {
  if (!isRecord(input) || !isStr(input.kind)) return null;
  if (!(RENDER_DIRECTIVE_KINDS as string[]).includes(input.kind)) return null;

  switch (input.kind as RenderDirectiveKind) {
    case 'metric':
      if (!isStr(input.label) || !isStr(input.value)) return null;
      if (!toneOk(input.tone)) return null;
      return input as unknown as MetricDirective;

    case 'chart': {
      if (!isStr(input.variant) || !(CHART_VARIANTS as string[]).includes(input.variant)) {
        return null;
      }
      if (!Array.isArray(input.points)) return null;
      const pointsOk = input.points.every(
        (p) => isRecord(p) && isStr(p.label) && typeof p.value === 'number' && isFinite(p.value)
      );
      if (!pointsOk) return null;
      return input as unknown as ChartDirective;
    }

    case 'table': {
      if (!isStrArray(input.columns) || !Array.isArray(input.rows)) return null;
      const rowsOk = input.rows.every((r) => isStrArray(r));
      if (!rowsOk) return null;
      return input as unknown as TableDirective;
    }

    case 'timeline': {
      if (!Array.isArray(input.items)) return null;
      const itemsOk = input.items.every(
        (it) => isRecord(it) && isStr(it.at) && isStr(it.title) && toneOk(it.tone)
      );
      if (!itemsOk) return null;
      return input as unknown as TimelineDirective;
    }

    case 'list':
      if (!isStrArray(input.items)) return null;
      return input as unknown as ListDirective;

    case 'callout':
      if (!isStr(input.text) || !toneOk(input.tone)) return null;
      return input as unknown as CalloutDirective;

    case 'actions': {
      if (!Array.isArray(input.actions)) return null;
      const actionsOk = input.actions.every(
        (a) => isRecord(a) && isStr(a.action) && isStr(a.label) && toneOk(a.tone)
      );
      if (!actionsOk) return null;
      return input as unknown as ActionsDirective;
    }

    case 'markdown':
      if (!isStr(input.text)) return null;
      return input as unknown as MarkdownDirective;

    default:
      return null;
  }
}

/**
 * Validate a batch of directives (e.g. `response.render_directives`). Drops
 * invalid entries silently and caps the result at `MAX_RENDER_DIRECTIVES`.
 * Always returns an array (possibly empty) — never throws.
 */
export function validateDirectives(input: unknown): RenderDirective[] {
  if (!Array.isArray(input)) return [];
  const out: RenderDirective[] = [];
  for (const item of input) {
    const valid = validateDirective(item);
    if (valid) out.push(valid);
    if (out.length >= MAX_RENDER_DIRECTIVES) break;
  }
  return out;
}
